import React, {FC, useState} from "react"

import {FormattedCurrency, FormattedMessage, InjectIntl, injectIntl} from "@irati/utils/lang"
import {TopUpTypes} from "@irati/wallet/enums"
import {poNumberSchema} from "@irati/wallet/helpers/validation"
import Card from "@irati/ui/src/Layout/Card"
import {H6, Body} from "@irati/ui/src/Typography"
import {Input, Checkbox, ValidationButton} from "@irati/ui/src/FormItem"
import {Row} from "react-bootstrap"
import Spacing from "@irati/ui/src/Spacing"
import Link from "@irati/ui/src/Link"
import Flex from "@irati/ui/src/Flex"
import mainTheme from "@irati/ui/src/global/theme"
import {Topup} from "@irati/types"
import ErrorText from "@irati/ui/src/ErrorText"
import tracking from "@irati-tracking/wallet/rewardPointsWallet"
import {TopUpOnChange, TopUpPayload} from "../../../containers/AdminReward/AdminRewardTopUp"
import DialogueModal from "./DialogueModal"
import BankTransferConfirmationModal from "./BankTransferConfirmationModal"

const PO_NUMBER = "PO Number"
const REWARD_WALLET_PAYMENT_TandC = "rewardWalletPaymentTandC"

interface Props extends InjectIntl {
  payload: TopUpPayload
  onPayloadChange: TopUpOnChange
  topupAmount: number
  createTopUp: () => Promise<void>
  goBackToTransactions: () => void
  rewardWalletTopup: Topup
}

const BankTransfer: FC<Props> = ({
  payload,
  onPayloadChange,
  topupAmount,
  createTopUp,
  goBackToTransactions,
  rewardWalletTopup,
  formatMessage
}) => {
  const [confirmedTandC, setConfirmedTandC] = useState<boolean>(false)
  const [showDialogModal, setShowDialogModal] = useState<boolean>(false)
  const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false)
  const [showErrorMessage, setShowErrorMessage] = useState<boolean>(false)
  const [agreeTandC, setAgreeTandC] = useState<boolean>(false)

  const handlePONumberUpdate = (arg: string): void => {
    onPayloadChange({...payload, poNumber: arg})
  }

  const toggleTermsAndConditions = () => {
    setConfirmedTandC(() => !confirmedTandC)
  }

  const closeCompletionModal = () => {
    setShowDialogModal(false)
  }

  const openConfirmationModal = async () => {
    tracking.bankTransfer()
    try {
      await createTopUp()
      tracking.bankTransferSuccess()
    } catch (e) {
      tracking.bankTransferError(e)
    } finally {
      closeCompletionModal()
      setShowConfirmationModal(true)
    }
  }

  const validateAmountAndOpenDialog = () => {
    if (topupAmount < 500) {
      setShowErrorMessage(true)
    } else {
      setShowDialogModal(true)
    }

    if (!confirmedTandC) {
      setAgreeTandC(true)
      setShowDialogModal(false)
    } else {
      setAgreeTandC(false)
    }
  }

  return (
    <Card small bgColor={mainTheme.colors.greys.lightest}>
      <H6 marginBottom>
        <FormattedMessage id="wallet.tenant.topUp.selectPaymentMethodName.bankTransfer" />
      </H6>

      {showErrorMessage && (
        <Spacing marginBottom={3}>
          <ErrorText>
            <FormattedMessage
              id="wallet.tenant.topUp.bankTransfer.errorText"
              values={{currency: <FormattedCurrency value="500" currency={payload.purchaseCurrency} />}}
            />
          </ErrorText>
        </Spacing>
      )}

      <Spacing paddingLeft={2}>
        <Body color={mainTheme.colors.greys.black}>
          <FormattedMessage id="wallet.reward.checkout.bankTransfer.card.body" />
        </Body>

        <Spacing marginTop={2} marginBottom={2}>
          <Row>
            <Input
              label="wallet.tenant.topUp.poNumber"
              name={PO_NUMBER}
              value={payload.poNumber}
              onChange={handlePONumberUpdate}
              validation={poNumberSchema}
            />
          </Row>
        </Spacing>

        <Flex marginTop={2} marginBottom={2}>
          <Checkbox
            checked={confirmedTandC}
            name={REWARD_WALLET_PAYMENT_TandC}
            onChange={toggleTermsAndConditions}
            value={TopUpTypes.BankTransfer}
          />
          <label htmlFor={REWARD_WALLET_PAYMENT_TandC}>
            <Body color={mainTheme.colors.greys.black}>
              <FormattedMessage id="wallet.tenant.topUp.termsAndConditions.firstSection" />
              <Link href={formatMessage({id: "wallet.tenant.topUp.termsAndConditions.url"})} target="_blank">
                <FormattedMessage id="wallet.tenant.topUp.termsAndConditions.link" />
              </Link>
              <FormattedMessage id="wallet.tenant.topUp.termsAndConditions.secondSection" />
            </Body>
          </label>
        </Flex>
        {agreeTandC && (
          <ErrorText>
            <FormattedMessage id="platform.defaultValidation.fieldsMustBeCompleted" />
          </ErrorText>
        )}

        <ValidationButton
          id="rewardWallet_topUp_bank_requestInvoice_button"
          primary
          full
          marginTop={2}
          onClick={validateAmountAndOpenDialog}>
          <FormattedMessage id="wallet.tenant.topUp.confirmationPopup.button.bank" />
        </ValidationButton>
      </Spacing>
      {showDialogModal && (
        <DialogueModal
          confirmedTandC
          closeCompletionModal={closeCompletionModal}
          payload={payload}
          topupAmount={topupAmount}
          openConfirmationModal={openConfirmationModal}
        />
      )}

      {showConfirmationModal && (
        <BankTransferConfirmationModal
          showConfirmationModal={showConfirmationModal}
          goBackToTransactions={goBackToTransactions}
          payload={payload}
          poNumber={payload.poNumber}
          rewardWalletTopup={rewardWalletTopup}
        />
      )}
    </Card>
  )
}

export default injectIntl(BankTransfer)
